/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import { Studio } from '../../../types/studio';

interface SingleStudioViewProps {
  studio: Studio;
  hasNotifications?: boolean;
  lastActivity?: string;
}

/**
 * SingleStudioView component for users who belong to exactly one studio.
 * Shows the studio prominently with quick actions and an option to add more studios.
 */
const SingleStudioView: React.FC<SingleStudioViewProps> = ({
  studio,
  hasNotifications = false,
}) => {
  const navigate = useNavigate();

  const handleDashboardClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click when clicking the button
    navigate(`/studio/${studio.id}`);
  };

  const handleCardClick = () => {
    navigate(`/studio/${studio.id}`);
  };

  const handleAddStudio = () => {
    navigate('/studios/add');
  };

  const formatLastActivity = (dateString?: string) => {
    if (!dateString) return 'No recent activity';

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return 'Active now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const getMemberCount = () =>
    // Use backend snake_case field first, fallback to camelCase
    studio.member_count ?? studio.memberCount ?? 0;
  const getLastActivity = () =>
    // Priority: enhanced activity fields > fallback to updated_at
    studio.last_activity ??
    studio.lastActivity ??
    studio.updated_at ??
    studio.updatedAt;
  const getActivityMessage = () => {
    const activityType = studio.activity_type ?? studio.activityType;
    const lastActivity = getLastActivity();

    if (!lastActivity) return 'No recent activity';

    const timeAgo = formatLastActivity(lastActivity);

    switch (activityType) {
      case 'member_joined':
        return `New member joined ${timeAgo}`;
      case 'membership_activity':
        return `Member activity ${timeAgo}`;
      case 'studio_updated':
        return `Studio updated ${timeAgo}`;
      default:
        return `Last activity ${timeAgo}`;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1">
            My Studio
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddStudio}
          >
            Add Studio
          </Button>
        </Box>

        {/* Main Studio Card */}
        <Card
          sx={{
            mb: 4,
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            },
          }}
          onClick={handleCardClick}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Studio Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3,
                mb: 3,
              }}
            >
              {/* Studio Logo */}
              <Avatar
                src={studio.logo}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                }}
              >
                {studio.name.charAt(0).toUpperCase()}
              </Avatar>

              {/* Studio Info */}
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" component="h2">
                    {studio.name}
                  </Typography>
                  {hasNotifications && (
                    <NotificationsIcon
                      color="primary"
                      sx={{ fontSize: 20 }}
                      titleAccess="You have new notifications"
                    />
                  )}
                </Box>

                {studio.description && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {studio.description}
                  </Typography>
                )}

                {/* Studio Stats */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    icon={<PeopleIcon />}
                    label={`${getMemberCount()} members`}
                    variant="outlined"
                    size="small"
                  />
                  {studio.subscriptionStatus && (
                    <Chip
                      label={studio.subscriptionStatus}
                      color={
                        studio.subscriptionStatus === 'active'
                          ? 'success'
                          : 'default'
                      }
                      size="small"
                    />
                  )}
                  {studio.isPersonal && (
                    <Chip
                      label="Personal Studio"
                      color="secondary"
                      size="small"
                    />
                  )}
                </Box>

                {/* Last Activity */}
                <Typography variant="caption" color="text.secondary">
                  {getActivityMessage()}
                </Typography>
              </Box>

              {/* Floating Dashboard Button */}
              <Tooltip title="Open Dashboard" arrow>
                <IconButton
                  onClick={handleDashboardClick}
                  size="large"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    alignSelf: 'flex-start',
                  }}
                  aria-label="Open studio dashboard"
                >
                  <DashboardIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default SingleStudioView;
